import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Cascader,
  Form,
  Input,
  Message,
  Modal,
  Popconfirm,
  Space,
  Table,
  type TableColumnProps,
} from '@arco-design/web-react';
import { IconDelete, IconEdit, IconPlus } from '@arco-design/web-react/icon';
import { PageHeader } from '@/components/ui';
import { billingApi } from '@/api';
import type { BillingProfile, BillingProfileDraft } from '@/types/billing';
import {
  EMAIL_REGEX,
  NAME_REGEX,
  PHONE_INTL_REGEX,
  POSTAL_CODE_REGEX,
} from '@/utils/validators';

const COUNTRY_OPTIONS = [
  { value: 'US', label: 'United States (US)' },
  { value: 'GB', label: 'United Kingdom (GB)' },
  { value: 'HK', label: 'Hong Kong (HK)' },
  { value: 'SG', label: 'Singapore (SG)' },
  { value: 'JP', label: 'Japan (JP)' },
  { value: 'CN', label: 'China (CN)' },
];

interface FormState {
  open: boolean;
  editing?: BillingProfile;
}

export default function BillingTemplatesView() {
  const [list, setList] = useState<BillingProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<FormState>({ open: false });
  const [form] = Form.useForm<BillingProfileDraft & { countryCascade?: string[] }>();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await billingApi.list({ page, pageSize, keyword: keyword || undefined });
      setList(result.items);
      setTotal(result.total);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, keyword]);

  useEffect(() => {
    fetchData().catch(() => undefined);
  }, [fetchData]);

  const openCreate = () => {
    form.resetFields();
    setFormState({ open: true });
  };

  const openEdit = (record: BillingProfile) => {
    form.resetFields();
    form.setFieldsValue({
      ...record,
      countryCascade: [record.country],
    });
    setFormState({ open: true, editing: record });
  };

  const handleSubmit = async () => {
    try {
      const v = await form.validate();
      const country = (v.countryCascade?.[0] ?? v.country)?.toUpperCase();
      const payload: BillingProfileDraft = {
        firstName: v.firstName,
        lastName: v.lastName,
        birthday: v.birthday,
        email: v.email,
        phone: v.phone,
        country: country!,
        state: v.state,
        city: v.city,
        street: v.street,
        postalCode: v.postalCode,
      };
      if (formState.editing) {
        await billingApi.update(formState.editing.id, payload);
        Message.success('已更新持卡人模板');
      } else {
        await billingApi.create(payload);
        Message.success('已创建持卡人模板');
      }
      setFormState({ open: false });
      fetchData();
    } catch (e) {
      if (e instanceof Error) Message.error(e.message);
    }
  };

  const columns: TableColumnProps<BillingProfile>[] = [
    {
      title: '姓名',
      dataIndex: 'firstName',
      render: (_, r) => `${r.firstName} ${r.lastName}`,
    },
    { title: '出生日期', dataIndex: 'birthday', width: 120 },
    { title: '风控邮箱', dataIndex: 'email', ellipsis: true },
    { title: '电话', dataIndex: 'phone', width: 160 },
    { title: '国家', dataIndex: 'country', width: 80 },
    {
      title: '地址',
      render: (_, r) => `${r.street}, ${r.city}, ${r.state} ${r.postalCode}`,
      ellipsis: true,
    },
    {
      title: '上游 Holder ID',
      dataIndex: 'upstreamHolderId',
      width: 200,
      render: (v) => v || <span style={{ color: '#86909C' }}>未登记</span>,
    },
    {
      title: '操作',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button size="mini" type="text" icon={<IconEdit />} onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确认删除？"
            content="若已被卡片引用，删除将失败"
            onOk={async () => {
              await billingApi.remove(record.id);
              Message.success('已删除');
              fetchData();
            }}
          >
            <Button size="mini" type="text" status="danger" icon={<IconDelete />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="up-page">
      <PageHeader
        title="持卡人地址模板"
        subtitle="集中维护客户预先登记的 KYC 模板，支持创建 / 修改 / 删除"
        extra={
          <Space>
            <Input.Search
              allowClear
              placeholder="按姓名 / 邮箱搜索"
              style={{ width: 240 }}
              onSearch={(v) => {
                setKeyword(v);
                setPage(1);
              }}
            />
            <Button type="primary" icon={<IconPlus />} onClick={openCreate}>
              新增持卡人
            </Button>
          </Space>
        }
      />

      <div className="up-section-card">
        <Table
          rowKey="id"
          loading={loading}
          data={list}
          columns={columns}
          pagination={{
            total,
            current: page,
            pageSize,
            sizeCanChange: true,
            showTotal: true,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
        />
      </div>

      <Modal
        title={formState.editing ? '编辑持卡人模板' : '新增持卡人模板'}
        visible={formState.open}
        onCancel={() => setFormState({ open: false })}
        onOk={handleSubmit}
        okText="保存"
        cancelText="取消"
        style={{ width: 640 }}
      >
        <Form form={form} layout="vertical">
          <Space style={{ width: '100%' }}>
            <Form.Item
              label="名 (First Name)"
              field="firstName"
              rules={[
                { required: true, message: '必填' },
                { match: NAME_REGEX, message: '仅允许英文 / 字母字符' },
              ]}
            >
              <Input placeholder="如 John" />
            </Form.Item>
            <Form.Item
              label="姓 (Last Name)"
              field="lastName"
              rules={[
                { required: true, message: '必填' },
                { match: NAME_REGEX, message: '仅允许英文 / 字母字符' },
              ]}
            >
              <Input placeholder="如 Doe" />
            </Form.Item>
          </Space>
          <Form.Item
            label="出生日期 (YYYY-MM-DD)"
            field="birthday"
            rules={[
              { required: true, message: '必填' },
              { match: /^\d{4}-\d{2}-\d{2}$/, message: '格式应为 YYYY-MM-DD' },
            ]}
          >
            <Input placeholder="1990-01-01" />
          </Form.Item>
          <Form.Item
            label="风控邮箱"
            field="email"
            rules={[
              { required: true, message: '必填' },
              { match: EMAIL_REGEX, message: '邮箱格式不正确' },
            ]}
          >
            <Input placeholder="holder@example.com" />
          </Form.Item>
          <Form.Item
            label="国际电话（含 + 号区号）"
            field="phone"
            rules={[
              { required: true, message: '必填' },
              {
                match: PHONE_INTL_REGEX,
                message: '必须以 + 开头，如 +1-415-555-0123',
              },
            ]}
          >
            <Input placeholder="+1-415-555-0123" />
          </Form.Item>
          <Form.Item
            label="国家 / 地区"
            field="countryCascade"
            rules={[{ required: true, message: '必选' }]}
          >
            <Cascader options={COUNTRY_OPTIONS} placeholder="ISO 两位国家码（自动大写）" />
          </Form.Item>
          <Space style={{ width: '100%' }}>
            <Form.Item label="州 / 省" field="state" rules={[{ required: true, message: '必填' }]}>
              <Input placeholder="如 CA" />
            </Form.Item>
            <Form.Item label="城市" field="city" rules={[{ required: true, message: '必填' }]}>
              <Input placeholder="如 San Francisco" />
            </Form.Item>
          </Space>
          <Form.Item label="详细街道" field="street" rules={[{ required: true, message: '必填' }]}>
            <Input placeholder="1 Market Street" />
          </Form.Item>
          <Form.Item
            label="邮政编码"
            field="postalCode"
            rules={[
              { required: true, message: '必填' },
              { match: POSTAL_CODE_REGEX, message: '邮编格式不正确' },
            ]}
          >
            <Input placeholder="如 94105" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
